import { useLogoutFunction } from "@propelauth/react";
import { LogOut, ShieldAlert } from "lucide-react";
import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { getNoneLayout } from "src/components/layout";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/card";
import { Label } from "src/components/ui/label";
import { Switch } from "src/components/ui/switch";
import { useMe } from "src/context/MeContext";
import { getSupportEmail, isSlackManagedByRootly } from "src/utils/brandTheme";

import type { SuspendedPageSetOrganizationSuspendedMutation } from "./__generated__/SuspendedPageSetOrganizationSuspendedMutation.graphql";

const setOrganizationSuspendedMutation = graphql`
  mutation SuspendedPageSetOrganizationSuspendedMutation($suspendedAt: DateTime) {
    setOrganizationSuspended(suspendedAt: $suspendedAt) {
      success {
        organization {
          id
          isSuspended
          suspendedAt
        }
      }
      error {
        message
      }
    }
  }
`;

function SuspendedPage() {
  const me = useMe();
  const isSuspended = Boolean(me.organization?.isSuspended);
  const supportEmail = getSupportEmail(me.organization?.brandTheme);
  const logout = useLogoutFunction();
  const [isUnsuspending, setIsUnsuspending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [commitSetOrganizationSuspended] = useMutation<SuspendedPageSetOrganizationSuspendedMutation>(setOrganizationSuspendedMutation);

  if (!me.isSuperAdmin && !isSuspended) {
    window.location.replace("/chat");
    return null;
  }

  // ROOTLY and ROOTLY_COBRAND orgs have their identity managed upstream by
  // Rootly SSO: in-app logout would be instantly re-authenticated, and their
  // app URL is ai-sre.rootly.com (not app.tierzero.ai). For those orgs we
  // hide Sign Out entirely. Other themes (including ROOTLY_HYBRID, which uses
  // TierZero auth on app.tierzero.ai) fall through to the normal PropelAuth
  // logout flow.
  const hideSignOut = isSlackManagedByRootly(me.organization?.brandTheme);

  const handleSignOut = async () => {
    localStorage.removeItem("tz_current_org_id");
    await logout(true);
  };

  const handleUnsuspend = () => {
    if (!me.isSuperAdmin) {
      return;
    }

    setIsUnsuspending(true);
    setMessage(null);

    commitSetOrganizationSuspended({
      variables: {
        suspendedAt: null,
      },
      onCompleted: (response) => {
        setIsUnsuspending(false);
        if (response.setOrganizationSuspended?.success) {
          window.location.replace("/chat");
          return;
        }

        setMessage(response.setOrganizationSuspended?.error?.message || "Failed to unsuspend organization.");
      },
      onError: (error) => {
        setIsUnsuspending(false);
        setMessage(error.message || "Failed to unsuspend organization.");
      },
    });
  };

  return (
    <div className="min-h-screen bg-background px-4 py-10 flex items-center justify-center">
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader className="items-center text-center">
            <div className="mb-2 rounded-full bg-destructive/10 p-3">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-xl">Organization Access Suspended</CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              Your organization's access has been suspended. To reactivate, please reach out to{" "}
              <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">
                {supportEmail}
              </a>.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {me.isSuperAdmin && isSuspended && (
              <div className="rounded-md border border-border bg-muted p-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="unsuspend-org"
                    checked={isSuspended}
                    onCheckedChange={(checked) => {
                      if (!checked) {
                        handleUnsuspend();
                      }
                    }}
                    disabled={isUnsuspending}
                  />
                  <Label htmlFor="unsuspend-org" className="text-foreground">
                    Lift suspension
                  </Label>
                  {isUnsuspending && (
                    <span className="text-sm text-muted-foreground">Restoring...</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">Super admin</span>
              </div>
            )}

            {message && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <p className="text-sm text-destructive">{message}</p>
              </div>
            )}

            {!hideSignOut && (
              <Button onClick={handleSignOut} variant="outline" className="w-full gap-2">
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}

SuspendedPage.getLayout = getNoneLayout;

export default SuspendedPage;
